/**
 * Build animated playback steps from an IQ scenario's static `court_setup`.
 *
 * IQ scenarios are stored as a single snapshot (initial positions + arrows
 * indicating intent). For the simulator we want to actually ANIMATE: arrows
 * fade in on step 0 (preview), then each arrow's "from" player slides to
 * "to" on step 1.
 *
 * Matching is by Euclidean proximity — pick the closest player to each
 * arrow.from inside a tolerance circle. This keeps the data schema simple
 * (arrows don't need to reference player ids) while producing the right
 * visual on every scenario.
 *
 * The first step also carries `description = situation` so PlayAnimation's
 * narrator reads the situation aloud while the play unfolds.
 */
const ARROW_MATCH_TOLERANCE_SQ = 144; // ~12 court units

export function buildIQScenarioSteps(courtSetup, situationText = '') {
  const players = (courtSetup?.players || []).map(p => ({ ...p }));
  const arrows = courtSetup?.arrows || [];

  if (players.length === 0 || arrows.length === 0) {
    // No movement to animate — return one static step so PlayAnimation
    // still renders the board.
    return [{
      step_number: 1,
      players,
      arrows,
      strokes: [],
      paths: {},
      audio_text: '',
      description: situationText,
    }];
  }

  // Step 1 — apply every arrow as a move on the closest matching player.
  const finalPlayers = players.map(p => ({ ...p }));
  arrows.forEach(arr => {
    if (!arr.from || !arr.to) return;
    let bestIdx = -1;
    let bestDist = Infinity;
    finalPlayers.forEach((pl, idx) => {
      const dx = pl.position.x - arr.from.x;
      const dy = pl.position.y - arr.from.y;
      const d = dx * dx + dy * dy;
      if (d < bestDist) { bestDist = d; bestIdx = idx; }
    });
    if (bestIdx >= 0 && bestDist < ARROW_MATCH_TOLERANCE_SQ) {
      finalPlayers[bestIdx] = {
        ...finalPlayers[bestIdx],
        position: { x: arr.to.x, y: arr.to.y },
      };
    }
  });

  return [
    {
      step_number: 1,
      players,
      arrows,
      strokes: [],
      paths: {},
      audio_text: '',
      description: situationText,
    },
    {
      step_number: 2,
      players: finalPlayers,
      arrows: [],
      strokes: [],
      paths: {},
      audio_text: '',
      description: '',
    },
  ];
}
